import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Box, Button, Flex, IconButton, Input, InputGroup, InputLeftElement, InputRightElement, Spinner, Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LuChevronDown, LuChevronLeft, LuChevronRight, LuMessageCircle, LuSearch, LuSlidersHorizontal,
  LuSparkles, LuTriangleAlert, LuX,
} from 'react-icons/lu';
import { useFilters } from '../../contexts/FilterContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { ConvertedAmountsProvider } from '../../contexts/ConvertedAmountsContext';
import {
  fetchGroupRows, fetchTags, groupMovements, searchMovements,
} from '../../api/endpointsBackend';
import useGroupedMovements from '../../hooks/useGroupedMovements';
import { useConvertedTotal } from '../../hooks/useConvertedTotal';
import { useMovementsData } from '../../hooks/useMovementsData';
import useSheetHistory from '../../hooks/useSheetHistory';
import { GROUPING_DAY, GROUPING_NONE, rowDimensionFor } from '../../hooks/useDisplaySettings';
import { DATE_MODES, formatDateRangeDisplay } from '../../utils/dateUtils';
import {
  dayShortUpper, labelToday, monthLong, monthShort, monthShortUpper,
} from '../../utils/intlFormatters';
import { formatFigure, formatPhoneBalance } from '../../utils/formatters';
import { todayLocalISO } from '../../utils/localDate';
import { isAiBudgetRefusal, limitRefusal } from '../../utils/aiUsage';
import { GROUP_COLORS } from '../../utils/movementDisplay';
import { reviewFilters } from '../../utils/movementAlerts';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import {
  formatChipValues, getFilterDisplayName, getOperatorLabel,
} from '../shared/filters/filterDisplayHelpers';
import { usePhoneShell, usePhoneTopBar } from './PhoneShellContext';
import { takeLinkedId } from '../../hooks/useUrlSyncedId';
import PhoneMovementRow from './PhoneMovementRow';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import PhoneFilterValueSheet from './PhoneFilterValueSheet';
import PhonePeriodSheet from './PhonePeriodSheet';

const TOP_BAR_H = '44px';
// The search bar that replaces the top bar is taller: a 44px field in a 52px row.
const SEARCH_BAR_H = '52px';
// Rows one day asks for at most; a day past it is not a day anyone scrolls.
const MAX_DAY_ROWS = 500;
const SEARCH_DEBOUNCE_MS = 300;
const HIGHLIGHT_MS = 1500;
// A saved movement that never shows here (another month) stops waiting.
const HIGHLIGHT_GIVE_UP_MS = 6000;

const TYPE_CHIPS = [
  { value: 'Expenses', key: 'expenses' },
  { value: 'Income', key: 'income' },
  { value: 'Transfer', key: 'transfers' },
];

// The value filters the phone draws as chips, "is any of" only.
const VALUE_CHIPS = [
  { id: 'account', entity: 'Movement', column: 'account_id' },
  { id: 'category', entity: 'Movement', column: 'category_id' },
  { id: 'tag', entity: 'Tag', column: 'id' },
];

// The account sheet's subline is the account's group, as the backend names
// it (legacy groups included) → the group label the Accounts page uses. A
// group missing here gets no subline rather than its raw English name.
const ACCOUNT_SUBLINE_KEYS = {
  savings: 'accounts:categories.Savings',
  Debit: 'accounts:categories.Savings',
  investment: 'accounts:categories.Investment',
  Investment: 'accounts:categories.Investment',
  Stocks: 'accounts:categories.Investment',
  Funds: 'accounts:categories.Investment',
  debt: 'accounts:categories.Debt',
  Credit: 'accounts:categories.Debt',
  Loan: 'accounts:categories.Debt',
  assets: 'accounts:categories.Assets',
  other: 'accounts:categories.Other',
  Lend: 'accounts:categories.Other',
};

const REVIEW_COLUMN = 'alert';
const NOT_IGNORED = { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] };

const is = (f, entity, column, operator) =>
  f.entity === entity && f.column === column && f.operator === operator;

const sameSet = (a = [], b = []) => {
  const left = new Set(a.map(String));
  const right = new Set(b.map(String));
  return left.size === right.size && [...left].every((v) => right.has(v));
};

/**
 * Sort the page's filters into what the phone can draw as chips and what it
 * cannot. The first "is any of" filter on type, account, category and tag
 * gets its chip; so does each name search. The alert filter is the To review
 * chip only when it names exactly `reviewValues` (the fixable flags), and then
 * the "not ignored" condition that travels with it is part of the chip.
 * Anything else, and every filter group, collapses into one read-only
 * "+N filters" chip.
 *
 * @returns {{ type, account, category, tag, review, name: Array, extra: number[] }}
 *   the filters themselves, and `extra` as indices into `selectedFilters`.
 */
export function classifyFilters(selectedFilters = [], reviewValues = null) {
  const out = { type: null, account: null, category: null, tag: null, review: null, name: [], extra: [] };
  const known = [
    ['type', 'Movement', 'type'],
    ['account', 'Movement', 'account_id'],
    ['category', 'Movement', 'category_id'],
    ['tag', 'Tag', 'id'],
  ];
  const isReview = (f) => Boolean(reviewValues?.length)
    && is(f, 'Movement', REVIEW_COLUMN, 'in') && sameSet(f.values, reviewValues);
  const hasReview = selectedFilters.some(isReview);
  let notIgnoredTaken = false;
  selectedFilters.forEach((f, index) => {
    const slot = known.find(([key, entity, column]) => !out[key] && is(f, entity, column, 'in'));
    if (slot) {
      out[slot[0]] = f;
    } else if (!out.review && isReview(f)) {
      out.review = f;
    } else if (is(f, 'Movement', 'note', 'ilike')) {
      out.name.push({ filter: f, index });
    } else if (hasReview && !notIgnoredTaken && is(f, 'Movement', 'is_ignored', 'eq')
      && (f.values || []).length === 1 && f.values[0] === false) {
      notIgnoredTaken = true;
    } else {
      out.extra.push(index);
    }
  });
  return out;
}

/**
 * A day header's label, in local terms and the US layout in both languages:
 * "TODAY · SUN, SEP 27", "SAT, SEP 26", "FRI, SEP 25, 2025".
 */
export function dayLabel(iso, now = new Date()) {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  const base = `${dayShortUpper(date.getDay())}, ${monthShortUpper(m - 1)} ${d}`
    + (y === now.getFullYear() ? '' : `, ${y}`);
  if (iso === todayLocalISO(now)) return `${labelToday()} · ${base}`;
  return base;
}

/**
 * A day total the way a row prints one: no code in the home currency, and
 * the cents dropped from four digits up, as Home and the month line do.
 */
function formatTotal(total, currency, home) {
  const abs = Math.abs(Number(total) || 0);
  const number = formatFigure(abs >= 1000 ? Math.round(abs) : abs);
  const text = currency && currency !== home ? `${currency} ${number}` : number;
  if (!total) return text;
  return `${total > 0 ? '+' : '−'}${text}`;
}

/**
 * A day header's total: `{ text, tone }`, text null when none shows.
 *
 * One figure when the day is in one currency (or converted), "≈" when
 * converted. Several currencies without a display currency list one figure
 * each, the home currency first as on the month line, leaving out a currency
 * that nets to zero (a transfer's) while another has a figure. A day holding
 * only transfers shows no total; a net of zero alone does not say so (income
 * can cancel expenses exactly), so its rows decide, and until they arrive
 * (`rows` null) such a day shows nothing rather than a guess.
 */
export function dayTotal(group, rows, home) {
  const counted = (rows || []).filter((m) => !m.is_ignored);
  const netsToZero = group.total === 0 && group.totalAmount > 0;
  const transferOnly = rows
    ? counted.length > 0 && counted.every((m) => m.type === 'Transfer')
    : netsToZero;
  const total = group.currency ? group.total : null;
  if (total != null) {
    if (transferOnly) return { text: null, tone: null };
    return {
      text: `${group.isConverted ? '≈ ' : ''}${formatTotal(total, group.currency, home)}`,
      tone: total > 0 ? 'income' : 'neutral',
    };
  }
  const entries = Object.entries(group.totalsByCurrency || {})
    .sort(([a], [b]) => (b === home) - (a === home));
  const nonZero = entries.filter(([, v]) => v);
  if (!entries.length) return { text: null, tone: null };
  return {
    text: (nonZero.length ? nonZero : entries).map(([cur, v]) => formatTotal(v, cur, home)).join(' · '),
    tone: 'neutral',
  };
}

/**
 * The search caption's period, mid-sentence: "September" (lower case in
 * Spanish, "septiembre"), "September 2025" outside the current year, the
 * range's own label otherwise, or null for All.
 */
function periodWords(dateRange, language) {
  if (dateRange?.mode === DATE_MODES.ALL) return null;
  if (dateRange?.mode === DATE_MODES.MONTH && dateRange.reference) {
    const { year, month } = dateRange.reference;
    const name = String(language).startsWith('es') ? monthLong(month).toLocaleLowerCase('es') : monthLong(month);
    return year === new Date().getFullYear() ? name : `${name} ${year}`;
  }
  return formatDateRangeDisplay(dateRange);
}

/**
 * The month line's two figures: `{ approximate, expenses, income }`, each
 * side a list of `{ currency, amount }`, or null while nothing is known.
 *
 * Converted into the display currency when one is set, marked approximate
 * when the backend says so or left movements out. Without one, every currency
 * the period holds, the home currency first: a figure in one currency must not
 * pass for the whole period.
 */
function useMonthLine({ filters, filterGroups, groupsOperator, startDate, endDate }, homeCurrency) {
  const converted = useConvertedTotal(filters, { startDate, endDate, groups: filterGroups, groupsOperator });
  const [native, setNative] = useState(null);
  const needsNative = !converted.displayCurrency;

  useEffect(() => {
    if (!needsNative) {
      setNative(null);
      return undefined;
    }
    const controller = new AbortController();
    groupMovements(filters, ['currency', 'type'], startDate, endDate, filterGroups, groupsOperator, null,
      { signal: controller.signal })
      .then((rows) => {
        const by = {};
        (rows || []).forEach((row) => {
          const entry = by[row.currency] || { currency: row.currency, expenses: 0, income: 0, count: 0 };
          if (row.type === 'Expenses') entry.expenses += Math.abs(row.total_amount ?? row.total ?? 0);
          if (row.type === 'Income') entry.income += Math.abs(row.total_amount ?? row.total ?? 0);
          entry.count += row.num_movements || 0;
          by[row.currency] = entry;
        });
        const entries = Object.values(by).sort((a, b) => (
          (b.currency === homeCurrency) - (a.currency === homeCurrency) || b.count - a.count
        ));
        const fallback = homeCurrency || entries[0]?.currency;
        const side = (key) => {
          const figures = entries.filter((e) => e[key]).map((e) => ({ currency: e.currency, amount: e[key] }));
          if (figures.length) return figures;
          return fallback ? [{ currency: fallback, amount: 0 }] : [];
        };
        setNative(fallback ? { approximate: false, expenses: side('expenses'), income: side('income') } : null);
      })
      .catch(() => { if (!controller.signal.aborted) setNative(null); });
    return () => controller.abort();
  }, [needsNative, filters, filterGroups, groupsOperator, startDate, endDate, homeCurrency]);

  if (converted.data) {
    const { data } = converted;
    const currency = data.display_currency;
    return {
      approximate: Boolean(data.approximate || data.num_unconvertible > 0),
      expenses: [{ currency, amount: data.expenses }],
      income: [{ currency, amount: data.income }],
    };
  }
  return native;
}

/**
 * The "To review" chip's N: the fixable review conditions together with every
 * other filter on the page and its period, so tapping the chip shows N rows.
 * The chip's own condition is swapped for the current one rather than doubled.
 * Read again after a phone save or fix and when the tab regains focus; the
 * last number stays up while a new one is on its way.
 */
function useChipReviewCount(reviewConditions, query, refreshSignal) {
  const { filters, filterGroups, groupsOperator, startDate, endDate } = query;
  const [count, setCount] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setTick((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    // Every fixable alert switched off: an empty alert list would match
    // every movement, so nothing is asked.
    if (!reviewConditions) {
      setCount(0);
      return undefined;
    }
    const values = reviewConditions[0].values;
    const others = (filters || []).filter((f) => !(is(f, 'Movement', REVIEW_COLUMN, 'in') && sameSet(f.values, values)));
    const controller = new AbortController();
    groupMovements([...others, ...reviewConditions], ['currency'], startDate, endDate, filterGroups, groupsOperator,
      null, { signal: controller.signal })
      .then((rows) => setCount((rows || []).reduce((sum, row) => sum + (row.num_movements || 0), 0)))
      .catch(() => {});
    return () => controller.abort();
  }, [reviewConditions, filters, filterGroups, groupsOperator, startDate, endDate, refreshSignal, tick]);

  return count;
}

// Stands in for the figure while a sentence is translated, so the words
// around it keep the locale's order and the figure alone takes the body colour.
const FIGURE_SLOT = '\u0000';

/**
 * "Spent COP 4,812,300 · Income COP 6,200,011": labels muted, figures in the
 * body colour, rounded as Home rounds them. Keyed on the shell's refresh
 * signal by its parent, so a phone save, fix or delete reads it again; until
 * the new figures arrive it keeps showing the last ones.
 */
function MonthLine({ query, home, lastRef }) {
  const { t } = useTranslation('movements');
  const line = useMonthLine(query, home);
  if (line) lastRef.current = line;
  const shown = line || lastRef.current;
  // A figure never breaks between its code and its number, so a second line
  // starts at a whole figure.
  const figures = (list) => `${shown.approximate ? '≈\u00a0' : ''}${list
    .map(({ currency, amount }) => formatPhoneBalance(currency, Math.abs(amount || 0)).replace(/ /g, '\u00a0'))
    .join(', ')}`;
  const part = (key, list) => {
    const [before, after = ''] = t(key, { amount: FIGURE_SLOT }).split(FIGURE_SLOT);
    return (
      <>
        {before}
        <Box as="span" color="redesign.text">{figures(list)}</Box>
        {after}
      </>
    );
  };
  return (
    // Two lines at most: at 320px in Spanish, or with several currencies,
    // the income half must not be cut off.
    <Text textStyle="phoneMeta" color="phone.muted" textAlign="center" minH="20px" noOfLines={2} px="16px">
      {shown && (
        <>
          {part('phone.list.spent', shown.expenses)}
          {' · '}
          {part('phone.list.income', shown.income)}
        </>
      )}
    </Text>
  );
}

/**
 * A 44px-tall chip with a 36px pill inside. With `onRemove` the pill ends in
 * a × that is its own 44px target, so the label still opens and × clears.
 * An alert chip keeps its orange border until it is on; its icon and count
 * carry the orange themselves.
 */
export function Chip({ active, tone, onClick, children, label, trailing, onRemove, removeLabel }) {
  const alert = tone === 'alert';
  let borderColor = 'redesign.border';
  if (active) borderColor = 'phone.link';
  else if (alert) borderColor = 'phone.alert';
  return (
    <Flex
      h="44px"
      align="center"
      flexShrink={0}
      sx={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <Flex
        align="center"
        h="36px"
        borderRadius="18px"
        border="1px solid"
        borderColor={borderColor}
        bg={active ? 'redesign.bg.accent' : 'redesign.bg.card'}
        color={active ? 'redesign.heading' : 'redesign.text'}
        fontSize="14px"
        fontWeight="500"
        whiteSpace="nowrap"
      >
        <Box
          as="button"
          type="button"
          onClick={onClick}
          aria-label={label}
          aria-pressed={active}
          display="flex"
          alignItems="center"
          gap="6px"
          h="44px"
          pl="13px"
          pr={onRemove ? '0' : '13px'}
        >
          {children}
          {trailing}
        </Box>
        {onRemove && (
          <Box
            as="button"
            type="button"
            onClick={onRemove}
            aria-label={removeLabel}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="44px"
            w="44px"
          >
            <Box as={LuX} boxSize="15px" />
          </Box>
        )}
      </Flex>
    </Flex>
  );
}

/** One day: a sticky header with the backend's totals, then its rows. */
function DayGroup({
  group, query, reloadToken, home, freshAbove, onRowsLoaded, onOpen, colorFor, stickyTop,
}) {
  const { t } = useTranslation('movements');
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const { filters, filterGroups, groupsOperator, startDate, endDate } = query;
  const count = group.count || 0;

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    (async () => {
      try {
        let items = [];
        let cursor = null;
        // A day is shown whole; the cursor loop only runs for a day holding
        // more rows than its aggregate counted (ignored ones).
        do {
          // eslint-disable-next-line no-await-in-loop
          const data = await fetchGroupRows({
            filters,
            groups: filterGroups,
            groupsOperator,
            scopes: [{ dimension: rowDimensionFor(GROUPING_DAY), value: group.value }],
            orderBy: 'date_desc',
            limit: Math.min(Math.max(count, 15), MAX_DAY_ROWS),
            cursor,
            start_date: startDate,
            end_date: endDate,
            signal: controller.signal,
          });
          items = [...items, ...(data.items || [])];
          cursor = data.next || null;
        } while (cursor && items.length < MAX_DAY_ROWS);
        if (controller.signal.aborted) return;
        setRows(items);
        onRowsLoaded(items);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setFailed(true);
      }
    })();
    return () => controller.abort();
  }, [group.value, count, filters, filterGroups, groupsOperator, startDate, endDate, reloadToken, onRowsLoaded]);

  const { text: totalText, tone: totalTone } = dayTotal(group, rows, home);
  const totalColor = totalTone === 'income' ? 'phone.income' : 'redesign.text';

  return (
    <Box as="section" aria-label={dayLabel(group.value)}>
      <Flex
        position="sticky"
        top={`calc(${stickyTop} + env(safe-area-inset-top))`}
        zIndex={2}
        h="36px"
        px="16px"
        align="center"
        justify="space-between"
        gap="12px"
        bg="redesign.bg.page"
      >
        {/* The date always shows; several currencies' totals give way to it. */}
        <Text
          fontSize="12px"
          lineHeight="16px"
          fontWeight="600"
          textTransform="uppercase"
          letterSpacing="0.05em"
          color="phone.muted"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          {dayLabel(group.value)}
        </Text>
        {totalText && (
          <Text
            fontSize="13px"
            lineHeight="18px"
            fontWeight="600"
            color={totalColor}
            minW={0}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {totalText}
          </Text>
        )}
      </Flex>

      {rows === null && !failed && (
        Array.from({ length: Math.min(Math.max(count, 1), 6) }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Flex key={i} h="64px" px="16px" align="center" gap="12px" aria-hidden>
            <Box w="40px" h="40px" borderRadius="full" bg="redesign.bg.card" />
            <Box flex="1">
              <Box h="14px" w="55%" borderRadius="4px" bg="redesign.bg.card" mb="8px" />
              <Box h="10px" w="35%" borderRadius="4px" bg="redesign.bg.card" />
            </Box>
          </Flex>
        ))
      )}
      {failed && (
        <Text textStyle="phoneMeta" color="phone.muted" px="16px" py="12px">{t('phone.list.error')}</Text>
      )}
      {(rows || []).map((movement) => (
        <PhoneMovementRow
          key={movement.id}
          movement={movement}
          homeCurrency={home}
          onClick={onOpen}
          highlight={freshAbove != null && movement.id > freshAbove}
          groupColor={movement.movement_group_id ? colorFor(movement.movement_group_id) : undefined}
        />
      ))}
    </Box>
  );
}

/** The read-only list of filters the phone cannot draw, with Clear. */
function ExtraFiltersBody({ filters, groups, filterOptions, onClear }) {
  const { t } = useTranslation('movements');
  const { close } = usePhoneSheet();
  const clear = async () => {
    await close();
    onClear();
  };
  return (
    <Box px="16px" pb="8px">
      <Text textStyle="phoneMeta" color="phone.muted" mb="12px">{t('phone.moreFilters.note')}</Text>
      <Box borderRadius="14px" bg="redesign.bg.card" border="1px solid" borderColor="redesign.border" overflow="hidden">
        {filters.map((f, i) => {
          const def = (filterOptions || []).find((o) => o.entity === f.entity && o.column === f.column);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={i} px="14px" py="12px" borderTop={i ? '1px solid' : 'none'} borderColor="redesign.border">
              <Text textStyle="phoneBody" color="redesign.heading">
                {getFilterDisplayName(f, filterOptions)}{' '}
                <Box as="span" color="phone.muted">{getOperatorLabel(f, def)}</Box>{' '}
                {formatChipValues(f, filterOptions)}
              </Text>
            </Box>
          );
        })}
        {groups.map((g, i) => (
          <Box
            key={g.id ?? `g${i}`}
            px="14px"
            py="12px"
            borderTop={filters.length || i ? '1px solid' : 'none'}
            borderColor="redesign.border"
          >
            <Text textStyle="phoneBody" color="redesign.heading">
              {t('phone.moreFilters.group', { count: (g.conditions || []).length })}
            </Text>
          </Box>
        ))}
      </Box>
      <Button variant="ghost" color="phone.expense" w="100%" h="48px" mt="12px" onClick={clear}>
        {t('phone.moreFilters.clear')}
      </Button>
    </Box>
  );
}

function PhoneMovementsInner() {
  const { t, i18n } = useTranslation('movements');
  const navigate = useNavigate();
  const {
    dateRange, selectedFilters, filterGroups, groupsOperator, apiFilters, apiFilterGroups, filterOptions,
    addFilter, removeFilter, removeFilterGroup, applyAiFilter, navigatePeriod, setDateMode, setCustomRange,
    goToToday,
  } = useFilters();
  const { alerts: alertPrefs } = usePreferences();
  const homeCurrency = usePhoneHomeCurrency();
  const { openMovement, refreshSignal } = usePhoneShell();
  const [isNarrow] = useMediaQuery('(max-width: 359.98px)', { ssr: false });

  const startDate = dateRange?.start_date || null;
  const endDate = dateRange?.end_date || null;
  const query = useMemo(() => ({
    filters: apiFilters, filterGroups: apiFilterGroups, groupsOperator, startDate, endDate,
  }), [apiFilters, apiFilterGroups, groupsOperator, startDate, endDate]);

  // Day grouping, newest first, straight through the hook: the desktop's
  // stored Display settings and their URL writes never run here.
  const grouped = useGroupedMovements({
    enabled: true,
    grouping: GROUPING_DAY,
    subgrouping: GROUPING_NONE,
    ordering: 'date_desc',
    filters: apiFilters,
    filterGroups: apiFilterGroups,
    groupsOperator,
    startDate,
    endDate,
  });
  const monthLineRef = useRef(null);

  // ---- After a save: refetch, and light up the row that is new ----------
  const maxSeenRef = useRef(0);
  const [freshAbove, setFreshAbove] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const freshTimer = useRef(null);
  const giveUpTimer = useRef(null);
  const firstSignal = useRef(refreshSignal);
  const refetchGroups = grouped.refetch;
  const groupCountRef = useRef(0);
  groupCountRef.current = grouped.groups.length;

  useEffect(() => {
    if (refreshSignal === firstSignal.current) return;
    firstSignal.current = refreshSignal;
    // Before any day's rows have loaded every row would count as new, so
    // nothing is lit.
    setFreshAbove(maxSeenRef.current === 0 && groupCountRef.current > 0 ? null : maxSeenRef.current);
    refetchGroups();
    setReloadToken((n) => n + 1);
    clearTimeout(giveUpTimer.current);
    giveUpTimer.current = setTimeout(() => setFreshAbove(null), HIGHLIGHT_GIVE_UP_MS);
  }, [refreshSignal, refetchGroups]);

  useEffect(() => () => {
    clearTimeout(freshTimer.current);
    clearTimeout(giveUpTimer.current);
  }, []);

  const freshAboveRef = useRef(freshAbove);
  freshAboveRef.current = freshAbove;
  const onRowsLoaded = useCallback((items) => {
    const ids = items.map((m) => m.id);
    const above = freshAboveRef.current;
    if (above != null && ids.some((id) => id > above)) {
      clearTimeout(freshTimer.current);
      freshTimer.current = setTimeout(() => setFreshAbove(null), HIGHLIGHT_MS);
    }
    maxSeenRef.current = Math.max(maxSeenRef.current, ...ids, 0);
  }, []);

  // Groups store no colour: the list hands out palette slots in the order it
  // meets them, the same rule the desktop list follows.
  const groupColors = useRef(new Map());
  const colorFor = useCallback((groupId) => {
    const map = groupColors.current;
    if (!map.has(groupId)) map.set(groupId, GROUP_COLORS[map.size % GROUP_COLORS.length]);
    return map.get(groupId);
  }, []);

  const openRow = useCallback((movement) => openMovement(movement.id), [openMovement]);

  // `?movement_id=<id>` (a movement in the chat, or a link from the desktop)
  // opens that movement's sheet over the list.
  useEffect(() => {
    const linked = takeLinkedId('movement_id');
    if (linked) openMovement(linked);
  }, [openMovement]);

  // ---- Chips --------------------------------------------------------------
  // Null when every fixable alert is switched off: an empty alert list would
  // match every movement.
  const reviewConditions = useMemo(() => reviewFilters(alertPrefs || {}).fixable, [alertPrefs]);
  const chips = useMemo(
    () => classifyFilters(selectedFilters, reviewConditions?.[0]?.values),
    [selectedFilters, reviewConditions],
  );
  const reviewCount = useChipReviewCount(reviewConditions, query, refreshSignal);
  const extraCount = chips.extra.length + (filterGroups || []).length;

  // Whether "not ignored" was already set when the chip went on, so turning
  // the chip off leaves a condition it did not add.
  const notIgnoredWasSet = useRef(false);
  const toggleReview = () => {
    if (chips.review) {
      addFilter({ ...chips.review, values: [] });
      if (!notIgnoredWasSet.current) addFilter({ ...NOT_IGNORED, values: [] });
      notIgnoredWasSet.current = false;
      return;
    }
    notIgnoredWasSet.current = selectedFilters.some((f) => is(f, 'Movement', 'is_ignored', 'eq')
      && (f.values || []).length === 1 && f.values[0] === false);
    (reviewConditions || []).forEach(addFilter);
  };

  const toggleType = (value) => {
    const current = chips.type?.values || [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    addFilter({ entity: 'Movement', column: 'type', operator: 'in', values: next });
  };

  const optionsFor = useCallback((entity, column) => (
    (filterOptions || []).find((o) => o.entity === entity && o.column === column)?.options || []
  ), [filterOptions]);

  // Tags reach the filter options only once a movement carries one, so the
  // tag sheet falls back to the workspace's tags.
  const [tagOptions, setTagOptions] = useState(null);
  const [openSheet, setOpenSheet] = useState(null);
  useEffect(() => {
    if (openSheet !== 'tag' || tagOptions || optionsFor('Tag', 'id').length) return;
    fetchTags(true)
      .then((tags) => setTagOptions((tags || []).map((tag) => ({ value: tag.id, displayName: tag.name }))))
      .catch(() => setTagOptions([]));
  }, [openSheet, tagOptions, optionsFor]);

  const sheetOptions = (chip) => {
    if (chip.id === 'tag') {
      const fromFilters = optionsFor('Tag', 'id');
      return fromFilters.length ? fromFilters : (tagOptions || []);
    }
    const options = optionsFor(chip.entity, chip.column);
    // The backend's subline is an enum (a category's type, an account's
    // kind); only its label is translated.
    const subKey = (sub) => (chip.id === 'category'
      ? `types.${String(sub).toLowerCase()}`
      : ACCOUNT_SUBLINE_KEYS[sub]);
    return options.map((o) => ({
      ...o,
      displaySubName: o.displaySubName && subKey(o.displaySubName)
        ? t(subKey(o.displaySubName), { defaultValue: o.displaySubName })
        : o.displaySubName,
    }));
  };

  const countFor = useCallback(async (chip, values) => {
    const others = apiFilters.filter((f) => !is(f, chip.entity, chip.column, 'in'));
    const next = values.length
      ? [...others, { entity: chip.entity, column: chip.column, operator: 'in', values }]
      : others;
    const data = await searchMovements(next, null, startDate, endDate, 1, 0, apiFilterGroups, groupsOperator);
    return data?.total_in_range ?? 0;
  }, [apiFilters, apiFilterGroups, groupsOperator, startDate, endDate]);
  const countForChip = useMemo(() => Object.fromEntries(
    VALUE_CHIPS.map((chip) => [chip.id, (values) => countFor(chip, values)]),
  ), [countFor]);

  const valueChipLabel = (chip) => {
    const filter = chips[chip.id];
    const label = t(`phone.chips.${chip.id}`);
    const values = filter?.values || [];
    if (values.length === 0) return label;
    if (values.length === 1) {
      const option = sheetOptions(chip).find((o) => String(o.value) === String(values[0]));
      return option?.displayName || label;
    }
    return t('phone.chips.many', { label, count: values.length });
  };

  const clearExtras = () => {
    [...chips.extra].sort((a, b) => b - a).forEach((index) => removeFilter(index));
    (filterGroups || []).forEach((g) => removeFilterGroup(g.id));
  };

  // Active chips lead the row, "+N filters" first, so a filter applied from
  // elsewhere (Home's categories, an account's review link) is on screen
  // without scrolling; the rest keep their order after them.
  const chipList = [];
  if (extraCount > 0) {
    chipList.push({
      key: 'extra',
      active: true,
      node: (
        <Chip active onClick={() => setOpenSheet('extra')}>
          <Box as={LuSlidersHorizontal} boxSize="15px" />
          {t('phone.chips.moreFilters', { count: extraCount })}
        </Chip>
      ),
    });
  }
  if (reviewCount > 0 || chips.review) {
    chipList.push({
      key: 'review',
      active: Boolean(chips.review),
      node: (
        <Chip
          tone="alert"
          active={Boolean(chips.review)}
          onClick={toggleReview}
          label={chips.review
            ? t('phone.chips.remove', { label: t('phone.chips.toReview', { count: reviewCount }) })
            : t('phone.chips.toReview', { count: reviewCount })}
          trailing={chips.review ? <Box as={LuX} boxSize="15px" /> : null}
        >
          {!chips.review && <Box as={LuTriangleAlert} boxSize="15px" color="phone.alert" />}
          {t('phone.chips.toReviewLabel')}
          <Box as="span" color="phone.alert" fontWeight="700">{reviewCount}</Box>
        </Chip>
      ),
    });
  }
  chips.name.forEach(({ filter }) => {
    const words = t('phone.chips.nameContains', { text: filter.values?.[0] });
    chipList.push({
      key: `name-${filter.values?.[0]}`,
      active: true,
      node: (
        <Chip
          active
          onClick={() => addFilter({ ...filter, values: [] })}
          label={t('phone.chips.remove', { label: words })}
          trailing={<Box as={LuX} boxSize="15px" />}
        >
          {words}
        </Chip>
      ),
    });
  });
  TYPE_CHIPS.forEach(({ value, key }) => {
    const active = (chips.type?.values || []).includes(value);
    chipList.push({
      key: `type-${value}`,
      active,
      node: (
        <Chip
          active={active}
          onClick={() => toggleType(value)}
          trailing={active ? <Box as={LuX} boxSize="15px" /> : null}
        >
          {t(`phone.chips.${key}`)}
        </Chip>
      ),
    });
  });
  VALUE_CHIPS.forEach((chip) => {
    const active = Boolean(chips[chip.id]);
    chipList.push({
      key: `value-${chip.id}`,
      active,
      node: (
        <Chip
          active={active}
          onClick={() => setOpenSheet(chip.id)}
          trailing={active ? null : <Box as={LuChevronDown} boxSize="15px" />}
          onRemove={active ? () => addFilter({ ...chips[chip.id], values: [] }) : undefined}
          removeLabel={t('phone.chips.remove', { label: valueChipLabel(chip) })}
        >
          {valueChipLabel(chip)}
        </Chip>
      ),
    });
  });
  const orderedChips = [...chipList.filter((c) => c.active), ...chipList.filter((c) => !c.active)];
  const activeChipKeys = orderedChips.filter((c) => c.active).map((c) => c.key).join('|');

  const chipRowRef = useRef(null);
  const [chipFade, setChipFade] = useState(false);
  const updateChipFade = useCallback(() => {
    const row = chipRowRef.current;
    if (!row) return;
    setChipFade(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
  }, []);
  // A chip that turns on moves to the front, so the row goes back to its start.
  useEffect(() => {
    chipRowRef.current?.scrollTo?.({ left: 0 });
  }, [activeChipKeys]);
  // The chips' widths change with every filter and language.
  useEffect(() => { updateChipFade(); });
  useEffect(() => {
    window.addEventListener('resize', updateChipFade);
    return () => window.removeEventListener('resize', updateChipFade);
  }, [updateChipFade]);

  // ---- Search -------------------------------------------------------------
  const [searching, setSearching] = useState(false);
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [aiState, setAiState] = useState('idle');
  // A refusal over a limit says which one (MI-354); any other failure the search's own words.
  const [aiFailure, setAiFailure] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(text.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);
  useEffect(() => { setAiState('idle'); }, [text]);

  const searchFilters = useMemo(() => (
    debounced ? [...apiFilters, { entity: 'Movement', column: 'note', operator: 'ilike', values: [debounced] }] : apiFilters
  ), [apiFilters, debounced]);
  const results = useMovementsData({
    dateRange,
    filters: searchFilters,
    filterGroups: apiFilterGroups,
    groupsOperator,
    ordering: 'date_desc',
    enabled: searching && Boolean(debounced),
  });
  const refetchResults = results.refetch;
  const searchActive = searching && Boolean(debounced);
  // Under an empty field the day list stays; the results take its place only
  // once there is text to search for, and they start at the top.
  const showResults = searchActive;
  useEffect(() => {
    if (showResults) window.scrollTo(0, 0);
  }, [showResults]);
  useEffect(() => {
    if (searchActive && refreshSignal) refetchResults();
  // Only a save re-reads the results; typing already does.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Where the list was when the search opened, to return there after it.
  const listScrollY = useRef(0);

  // The field is mounted by this tap and focused inside it: iOS raises the
  // keyboard only for a focus() made during the user's gesture.
  const startSearch = useCallback(() => {
    listScrollY.current = window.scrollY;
    flushSync(() => setSearching(true));
    searchRef.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, []);
  const clearSearchText = () => {
    setText('');
    searchRef.current?.focus({ preventScroll: true });
  };
  const endSearch = () => {
    searchRef.current?.blur();
    flushSync(() => {
      setSearching(false);
      setText('');
      setDebounced('');
      setAiState('idle');
    });
    const y = listScrollY.current;
    requestAnimationFrame(() => window.scrollTo(0, y));
  };

  // The search takes a history entry like a sheet, so the back swipe cancels
  // it instead of leaving the page. Every way out goes through close(), and a
  // filter or a navigation waits for the entry to be gone: the pending back
  // step would otherwise undo it. While the AI filter runs the entry is
  // already gone (`aiHoldsSearch`), and it comes back if the AI fails.
  const aiHoldsSearch = useRef(false);
  const { close: closeSearch } = useSheetHistory('movements-search', searching && aiState !== 'loading', {
    onPop: () => { if (!aiHoldsSearch.current) endSearch(); },
  });

  const keepAsChip = async () => {
    const value = text.trim();
    if (!value) return;
    // A new filter starts the list from the top.
    listScrollY.current = 0;
    await closeSearch();
    addFilter({ entity: 'Movement', column: 'note', operator: 'ilike', values: [value] });
  };

  const filterWithAi = async () => {
    const value = text.trim();
    // Under an empty field the two rows only point back to it.
    if (!value) {
      searchRef.current?.focus({ preventScroll: true });
      return;
    }
    if (aiState === 'loading') return;
    aiHoldsSearch.current = true;
    await closeSearch();
    aiHoldsSearch.current = false;
    setAiState('loading');
    setAiFailure(null);
    try {
      const ok = await applyAiFilter(value);
      if (ok) {
        listScrollY.current = 0;
        endSearch();
      }
      else setAiState('failed');
    } catch (err) {
      setAiFailure(err);
      setAiState('failed');
    }
  };

  const askMiKapital = async () => {
    const value = text.trim();
    if (!value) {
      searchRef.current?.focus({ preventScroll: true });
      return;
    }
    await closeSearch();
    navigate('/chat', { state: { ask: value } });
  };

  usePhoneTopBar({
    hidden: searching,
    right: (
      <IconButton
        icon={<LuSearch size={22} />}
        variant="ghost"
        borderRadius="full"
        color="redesign.heading"
        minW="44px"
        minH="44px"
        aria-label={t('phone.search.open')}
        onClick={startSearch}
      />
    ),
  }, [searching, i18n.language, startSearch]);

  // ---- Period ---------------------------------------------------------------
  const [periodOpen, setPeriodOpen] = useState(false);
  const canStep = dateRange?.mode === DATE_MODES.MONTH
    || dateRange?.mode === DATE_MODES.WEEK
    || dateRange?.mode === DATE_MODES.YEAR;
  const periodLabel = isNarrow && dateRange?.mode === DATE_MODES.MONTH && dateRange.reference
    ? `${monthShort(dateRange.reference.month)} ${dateRange.reference.year}`
    : formatDateRangeDisplay(dateRange);

  const hasFilters = selectedFilters.length > 0 || (filterGroups || []).length > 0;

  // From a period that holds today, a new mode lands on today's week, month
  // or year: the phone has no Today button to get back to it. From any other
  // period it keeps that period's place.
  const pickMode = (mode) => {
    const today = todayLocalISO();
    const holdsToday = !dateRange?.start_date
      || (dateRange.start_date <= today && today <= (dateRange.end_date || today));
    setDateMode(mode);
    if (holdsToday && mode !== DATE_MODES.ALL) goToToday();
  };

  // ---- Render -------------------------------------------------------------
  const searchValue = text.trim();
  const searchPeriod = periodWords(dateRange, i18n.language);

  return (
    <Box>
      {searching && (
        <>
          <Flex
            as="form"
            role="search"
            onSubmit={(e) => { e.preventDefault(); keepAsChip(); }}
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex="sticky"
            bg="redesign.bg.page"
            pt="env(safe-area-inset-top)"
            pl="calc(env(safe-area-inset-left) + 16px)"
            pr="calc(env(safe-area-inset-right) + 4px)"
          >
            <Flex h={SEARCH_BAR_H} align="center" gap="4px" w="100%">
              <InputGroup flex="1" minW={0} size="md">
                <InputLeftElement pointerEvents="none" h="44px" color="phone.muted">
                  <LuSearch size={18} />
                </InputLeftElement>
                <Input
                  ref={searchRef}
                  type="search"
                  enterKeyHint="search"
                  autoCorrect="off"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
                  placeholder={t('phone.search.placeholder')}
                  aria-label={t('phone.search.placeholder')}
                  h="44px"
                  fontSize="16px"
                  borderRadius="12px"
                  bg="redesign.bg.card"
                  borderColor="phone.link"
                  pr={text ? '44px' : '12px'}
                  _hover={{ borderColor: 'phone.link' }}
                  _focusVisible={{ borderColor: 'phone.link', boxShadow: '0 0 0 1px var(--chakra-colors-phone-link)' }}
                  // The browser's own clear control is unthemed, too small to
                  // tap and missing on iOS; the field draws its own.
                  sx={{
                    '&::-webkit-search-cancel-button': { display: 'none', WebkitAppearance: 'none' },
                    '&::-webkit-search-decoration': { display: 'none', WebkitAppearance: 'none' },
                  }}
                />
                {text && (
                  <InputRightElement h="44px" w="44px">
                    <Box
                      as="button"
                      type="button"
                      aria-label={t('phone.search.clear')}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w="44px"
                      h="44px"
                      // Keeps the field focused, so the keyboard stays up.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={clearSearchText}
                    >
                      <Flex
                        align="center"
                        justify="center"
                        w="18px"
                        h="18px"
                        borderRadius="full"
                        bg="redesign.text.faint"
                        color="redesign.bg.page"
                      >
                        <Box as={LuX} boxSize="11px" strokeWidth={3.2} />
                      </Flex>
                    </Box>
                  </InputRightElement>
                )}
              </InputGroup>
              <Button
                variant="ghost"
                color="phone.link"
                fontWeight="500"
                fontSize="16px"
                px="12px"
                minH="44px"
                flexShrink={0}
                _hover={{ bg: 'transparent' }}
                onClick={closeSearch}
              >
                {t('phone.search.cancel')}
              </Button>
            </Flex>
          </Flex>

          {/* The field is taller than the top bar it replaces. */}
          <Box pt={`calc(${SEARCH_BAR_H} - ${TOP_BAR_H} + 8px)`}>
            <Box mx="16px" borderRadius="14px" bg="redesign.bg.surface" border="1px solid" borderColor="redesign.border" overflow="hidden">
              <SearchAction icon={LuSparkles} onClick={filterWithAi} busy={aiState === 'loading'}>
                {searchValue ? t('phone.search.aiFilter', { text: searchValue }) : t('phone.search.aiFilterEmpty')}
              </SearchAction>
              <Box h="1px" bg="redesign.border" ml="50px" />
              <SearchAction icon={LuMessageCircle} onClick={askMiKapital}>
                {searchValue ? t('phone.search.ask', { text: searchValue }) : t('phone.search.askEmpty')}
              </SearchAction>
            </Box>
            {aiState === 'failed' && (
              <Text textStyle="phoneMeta" color="phone.alert" px="16px" pt="8px" role="status">
                {limitRefusal(aiFailure) || t('phone.search.aiFailed')}
              </Text>
            )}
            {aiState === 'failed' && isAiBudgetRefusal(aiFailure) && (
              <Text textStyle="phoneMeta" color="phone.muted" px="16px" pt="4px">
                {t('common:aiBudget.pickFilters')}
              </Text>
            )}
            {showResults && (
              <>
                {!results.loading && (
                  <Text textStyle="phoneMeta" fontWeight="500" color="phone.muted" px="16px" pt="18px" pb="6px">
                    {results.movementsList.length
                      ? (searchPeriod
                        ? t('phone.search.countIn', { count: results.paginationInfo.totalInRange, period: searchPeriod })
                        : t('phone.search.countAll', { count: results.paginationInfo.totalInRange }))
                      : (searchPeriod
                        ? t('phone.search.noResults', { period: searchPeriod })
                        : t('phone.search.noResultsAll'))}
                  </Text>
                )}
                {results.loading && (
                  <Flex justify="center" py="20px"><Spinner size="sm" color="phone.muted" /></Flex>
                )}
                {!results.loading && results.movementsList.map((movement) => (
                  <PhoneMovementRow
                    key={movement.id}
                    movement={movement}
                    homeCurrency={homeCurrency}
                    showDate
                    onClick={openRow}
                  />
                ))}
                {results.hasMore && !results.loading && (
                  <Box px="16px" pt="8px">
                    <Button variant="outline" w="100%" h="48px" isLoading={results.loadingMore} onClick={results.loadMore}>
                      {t('phone.list.loadEarlier')}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* The day list stays mounted through a search: under the empty field
          it shows as it is, and leaving the search returns to the same place. */}
      <Box hidden={showResults} pt={searching ? '8px' : 0}>
        <Box hidden={searching}>
          {/* Period bar */}
          <Flex h="44px" align="center" justify="center" px="8px" gap="4px">
            <IconButton
              icon={<LuChevronLeft size={22} />}
              variant="ghost"
              borderRadius="full"
              minW="44px"
              minH="44px"
              aria-label={t('filters:dateRange.aria.previousPeriod')}
              isDisabled={!canStep}
              onClick={() => navigatePeriod('prev')}
            />
            <Button
              variant="ghost"
              minH="44px"
              // A short label ("All", "Todo") still gets a 44px target.
              minW="44px"
              px="12px"
              fontSize="16px"
              fontWeight="600"
              color="redesign.heading"
              // A touch leaves :hover behind after the tap; only the press shows.
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'redesign.bg.hover' }}
              aria-label={`${periodLabel}. ${t('phone.period.open')}`}
              onClick={() => setPeriodOpen(true)}
            >
              <Text as="span" noOfLines={1}>{periodLabel}</Text>
            </Button>
            <IconButton
              icon={<LuChevronRight size={22} />}
              variant="ghost"
              borderRadius="full"
              minW="44px"
              minH="44px"
              aria-label={t('filters:dateRange.aria.nextPeriod')}
              isDisabled={!canStep}
              onClick={() => navigatePeriod('next')}
            />
          </Flex>

          {/* Month line */}
          <MonthLine key={refreshSignal} query={query} home={homeCurrency} lastRef={monthLineRef} />

          {/* Chips */}
          <Box position="relative">
            <Flex
              ref={chipRowRef}
              role="group"
              aria-label={t('phone.chips.label')}
              gap="8px"
              px="16px"
              overflowX="auto"
              onScroll={updateChipFade}
              sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
            >
              {orderedChips.map(({ key, node }) => (
                <React.Fragment key={key}>{node}</React.Fragment>
              ))}
            </Flex>
            {/* The row runs on past the edge: a fade says so. */}
            <Box
              hidden={!chipFade}
              aria-hidden
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              w="40px"
              pointerEvents="none"
              sx={{ background: 'linear-gradient(90deg, transparent, var(--chakra-colors-redesign-bg-page))' }}
            />
          </Box>
        </Box>

        {/* Days */}
        <Box>
          {grouped.loading && grouped.groups.length === 0 && (
            <Flex justify="center" py="32px"><Spinner color="phone.muted" /></Flex>
          )}
          {grouped.error && !grouped.loading && (
            <Flex direction="column" align="center" gap="8px" py="32px" px="16px">
              <Text textStyle="phoneBody" color="phone.muted">{t('phone.list.error')}</Text>
              <Button variant="outline" onClick={refetchGroups}>{t('phone.list.retry')}</Button>
            </Flex>
          )}
          {!grouped.loading && !grouped.error && grouped.groups.length === 0 && (
            <Text textStyle="phoneBody" color="phone.muted" textAlign="center" px="16px" py="40px">
              {hasFilters ? t('phone.list.emptyFiltered') : t('phone.list.empty')}
            </Text>
          )}
          {grouped.groups.map((group) => (
            <DayGroup
              key={group.key}
              group={group}
              query={query}
              reloadToken={reloadToken}
              home={homeCurrency}
              freshAbove={freshAbove}
              onRowsLoaded={onRowsLoaded}
              onOpen={openRow}
              colorFor={colorFor}
              stickyTop={searching ? SEARCH_BAR_H : TOP_BAR_H}
            />
          ))}
          {grouped.hasMoreGroups && (
            <Box px="16px" pt="12px">
              <Button
                variant="outline"
                w="100%"
                h="48px"
                isLoading={grouped.loadingMore}
                onClick={grouped.loadMoreGroups}
              >
                {t('phone.list.loadEarlier')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <PhonePeriodSheet
        isOpen={periodOpen}
        onClose={() => setPeriodOpen(false)}
        dateRange={dateRange}
        onMode={pickMode}
        onCustom={setCustomRange}
      />
      {VALUE_CHIPS.map((chip) => (
        <PhoneFilterValueSheet
          key={chip.id}
          id={`movements-filter-${chip.id}`}
          isOpen={openSheet === chip.id}
          onClose={() => setOpenSheet(null)}
          title={t(`phone.chips.${chip.id}`)}
          options={sheetOptions(chip)}
          selected={chips[chip.id]?.values || []}
          countFor={countForChip[chip.id]}
          onApply={(values) => addFilter({ entity: chip.entity, column: chip.column, operator: 'in', values })}
        />
      ))}
      <PhoneSheet
        id="movements-extra-filters"
        isOpen={openSheet === 'extra'}
        onClose={() => setOpenSheet(null)}
        title={t('phone.moreFilters.title')}
      >
        <ExtraFiltersBody
          filters={chips.extra.map((i) => selectedFilters[i])}
          groups={filterGroups || []}
          filterOptions={filterOptions}
          onClear={clearExtras}
        />
      </PhoneSheet>
    </Box>
  );
}

/** A 52px action row under the search field. */
function SearchAction({ icon, onClick, busy, children }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="grid"
      gridTemplateColumns="22px minmax(0, 1fr) 18px"
      columnGap="14px"
      alignItems="center"
      w="100%"
      minH="52px"
      px="14px"
      textAlign="left"
      color="redesign.heading"
      _active={{ bg: 'redesign.bg.hover' }}
    >
      {busy ? <Spinner size="sm" color="phone.link" /> : <Box as={icon} boxSize="20px" color="phone.link" />}
      <Text textStyle="phoneBody" noOfLines={2}>{children}</Text>
      <Box as={LuChevronRight} boxSize="18px" color="phone.muted" />
    </Box>
  );
}

/**
 * The Movements tab on the phone: the period, the month's two figures, filter
 * chips, and the movements by day, newest first, each day with its totals.
 * A tap on a row opens it in the movement sheet; 🔍 searches by name and hands
 * off to Filter with AI or Ask.
 */
export default function PhoneMovements() {
  return (
    <ConvertedAmountsProvider>
      <PhoneMovementsInner />
    </ConvertedAmountsProvider>
  );
}
